/**
 * 5897 — Como o público compra: resultados do estudo de inteligência de vendas
 * (canais, curvas de venda, viradas de lote, artistas por mercado, produtores).
 * Números fixos do estudo, servidos pelo analytics (artefato estudo.json.gz) atrás
 * do PV1 + usuário BD; não depende do snapshot diário.
 */
import { FC } from 'react'
import styled from 'styled-components'
import { BD, PageSubtitle, PageTitle, Table } from '../styles'
import { EstudoPrevisoes } from '../../../types/previsoes'
import { CartaoTabela, ComEstudo, Entenda, Kpi, Kpis, Rolagem } from './comum'
import { GraficoBarras, GraficoEmpilhado, GraficoFaixaLog, GraficoLinhas, pctFmt } from './graficos'

const Secao = styled.h2`
  font-size: 18px;
  font-weight: 800;
  margin: 32px 0 6px;
`
const Grade2 = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 440px), 1fr));
  gap: 16px;
  margin-bottom: 16px;
`
const Texto = styled.p`
  font-size: 14px;
  line-height: 1.55;
  color: ${BD.black};
  max-width: 90ch;
  margin: 0 0 12px;
`

const n = (v: number): string => v.toLocaleString('pt-BR')
const reais = (v: number): string => `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`
const razao = (v: number): string => v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })

export const PrevisoesEstudo: FC = () => <ComEstudo>{(estudo) => <Conteudo estudo={estudo} />}</ComEstudo>

const Conteudo: FC<{ estudo: EstudoPrevisoes }> = ({ estudo }) => {
  const {
    ANOS,
    ANO_A_ANO,
    ARTISTA_UF,
    CANAL,
    CURVA_PERIODO,
    CURVA_PORTE,
    DATA_ESTUDO,
    DIAS,
    ESTILOS,
    MOTIVO_VIRADA,
    PANDEMIA,
    PERFIS_PRODUTOR,
    RITMO_POS_VIRADA,
  } = estudo
  const eixoDias = {
    xs: DIAS,
    invertido: true,
    ymax: 1,
    yticks: [0, 0.25, 0.5, 0.75, 1],
    xticks: [60, 45, 30, 14, 7, 0],
    fmtX: (t: number) => (t ? `D-${t}` : 'D0'),
    tituloDica: (i: number) => (DIAS[i] ? `D-${DIAS[i]}` : 'Dia do evento'),
  }
  return (
    <>
      <PageTitle>Como o público compra</PageTitle>
      <PageSubtitle>
        Resultados do estudo de inteligência de vendas: eventos realizados com ≥100 pagos, de 2019 a out/2026 (dados até{' '}
        {DATA_ESTUDO}). 2020 e 2021 aparecem marcados como pandemia: ficam fora do treino e da validação dos modelos e da lista de eventos comparáveis, mas aparecem nos gráficos para comparação.
      </PageSubtitle>
      <Kpis>
        <Kpi rotulo="Vendas pelo site" valor="16% → 75%" dica="fatia dos pagos vendida pelo site, de 2019 a 2026" />
        <Kpi rotulo="Vendido até D-7" valor="45%" dica="no evento típico pós-pandemia" />
        <Kpi rotulo="Viradas por decisão do produtor" valor="68,5%" dica="desde mar/2024; só 21% por esgotamento" />
        <Kpi rotulo="Erro da projeção em D-7" valor="17,6%" dica="contra 29,6% da melhor regra simples" />
      </Kpis>

      <Secao>2019 a 2026, ano a ano</Secao>
      <Grade2>
        <GraficoEmpilhado
          titulo="Por onde os ingressos pagos foram vendidos"
          sub="% dos pagos por canal, por ano do evento · fundo claro = pandemia"
          categorias={ANOS}
          series={CANAL}
          destaque={PANDEMIA}
          rotuloDestaque="pandemia"
          entenda={[
            'Cada barra é um ano e soma 100% dos pagos daquele ano: site, PDV (bilheteria e pontos físicos) e comissários.',
            'Em 2019 quase 8 de cada 10 ingressos saíam do PDV; em 2026, três de cada quatro saem do site. A mudança acelerou durante e logo depois da pandemia.',
            'Comparar um indicador de 2019 com um de 2025 sem separar por canal mistura comportamentos diferentes.',
          ]}
        />
        <CartaoTabela>
          <div className="titulo">
            <h3>Indicadores por ano</h3>
            <div style={{ fontSize: 12.5, color: BD.grayText }}>Sem temporadas e eventos de vários dias · preços nominais</div>
          </div>
          <Rolagem>
            <Table>
              <thead>
                <tr>
                  <th>Ano</th>
                  <th className="n">Eventos</th>
                  <th className="n">Pagos</th>
                  <th className="n">Janela</th>
                  <th className="n">No dia</th>
                  <th className="n">Preço médio</th>
                  <th className="n">Cortesias</th>
                </tr>
              </thead>
              <tbody>
                {ANO_A_ANO.map(([ano, eventos, pagos, janela, noDia, , preco, cortesias]) => (
                  <tr key={ano} style={{ cursor: 'default', color: ano.includes('pandemia') ? BD.grayText : undefined }}>
                    <td>{ano}</td>
                    <td className="n">{n(eventos)}</td>
                    <td className="n">{pagos.toLocaleString('pt-BR')} mi</td>
                    <td className="n">{janela} dias</td>
                    <td className="n">{pctFmt(noDia)}</td>
                    <td className="n">{reais(preco)}</td>
                    <td className="n">{pctFmt(cortesias)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Rolagem>
        </CartaoTabela>
      </Grade2>

      <Secao>Quando o público compra</Secao>
      <Grade2>
        <GraficoLinhas
          titulo="Fração vendida até cada dia, por período"
          sub="Mediana por evento da fração acumulada do total final"
          series={CURVA_PERIODO}
          {...eixoDias}
          entenda={[
            'Da esquerda (60 dias antes) para a direita (dia do evento), quanto do total final já estava vendido. Cada linha é o evento típico de um período; a tracejada é a pandemia.',
            'Depois da pandemia o público compra um pouco mais cedo: 45% vendido uma semana antes, contra 40% em 2019. Na pandemia, quase nada saía com mais de 14 dias.',
            'É o evento típico: metade fica acima da linha e metade abaixo. O porte muda muito a curva (gráfico ao lado).',
          ]}
        />
        <GraficoLinhas
          titulo="Pós-pandemia, por porte do evento"
          sub="Mediana por evento da fração acumulada · 2022–2026"
          series={CURVA_PORTE}
          {...eixoDias}
          entenda={[
            'O porte é o total de pagos que o evento vendeu no fim.',
            'No evento típico de 10 mil+, 57% já estava vendido 30 dias antes. No de 100 a 499 pagos, quase nada estava vendido 30 dias antes, e um quarto sai no próprio dia.',
            'O porte só é conhecido no fim; para um evento em andamento, a projeção usa os comparáveis e o modelo.',
          ]}
        />
      </Grade2>
      <Texto>
        Funk é o estilo mais de última hora, com 31% no dia; Rock e Réveillon são os mais antecipados (20–23% vendido
        mais de 30 dias antes). Comparando eventos do mesmo porte e estilo, só o Sul compra de fato mais cedo (+3 dias).
        Feriados nacionais não mostraram efeito (1,05×), e camarote/premium vendem só 3–4 p.p. menos no dia que a pista.
      </Texto>
      <CartaoTabela style={{ marginBottom: 16 }}>
        <div className="titulo">
          <h3>Antecedência por estilo (pós-pandemia)</h3>
        </div>
        <Rolagem>
          <Table>
            <thead>
              <tr>
                <th>Estilo</th>
                <th className="n">Eventos</th>
                <th className="n">% no dia</th>
                <th className="n">% última semana</th>
                <th className="n">% &gt;30 dias</th>
                <th className="n">Antecedência mediana</th>
              </tr>
            </thead>
            <tbody>
              {ESTILOS.map(([estilo, eventos, noDia, semana, mais30, antecedencia]) => (
                <tr key={estilo} style={{ cursor: 'default' }}>
                  <td>{estilo}</td>
                  <td className="n">{n(eventos)}</td>
                  <td className="n">{pctFmt(noDia)}</td>
                  <td className="n">{pctFmt(semana)}</td>
                  <td className="n">{pctFmt(mais30)}</td>
                  <td className="n">{antecedencia} dias</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Rolagem>
      </CartaoTabela>

      <Secao>Viradas de lote</Secao>
      <Texto>
        O sistema não grava a virada nem o motivo; só um carimbo de data, que também aparece quando o produtor reduz a
        quantidade até o que já foi vendido. O motivo foi inferido cruzando o carimbo com os salvamentos da tela Valores
        (histórico só desde 2024).
      </Texto>
      <Grade2>
        <GraficoBarras
          titulo="Motivo inferido das viradas"
          sub="% das 32.757 viradas em eventos desde mar/2024"
          categorias={MOTIVO_VIRADA.categorias}
          valores={MOTIVO_VIRADA.valores}
          max={0.8}
          ticks={[0, 0.25, 0.5, 0.75]}
          entenda={[
            'Se o carimbo da virada cai a até 2 minutos de um salvamento na tela Valores, a virada foi manual; se não, foi venda.',
            'Dois de cada três lotes viram por decisão do produtor, com em mediana só 42% vendido. Só 21% são esgotamento de verdade.',
            'Prever quando um lote esgota não é prever quando ele vira: na maioria das vezes quem decide é o produtor.',
          ]}
        />
        <GraficoBarras
          titulo="Ritmo depois da virada, por mudança de preço"
          sub="Venda diária 7 dias depois ÷ 7 dias antes (mediana) · 1 = sem mudança"
          categorias={RITMO_POS_VIRADA.categorias}
          valores={RITMO_POS_VIRADA.valores}
          max={1.4}
          ticks={[0, 0.5, 1]}
          referencia={1}
          fmt={razao}
          entenda={[
            'Abaixo de 1, o ritmo caiu (0,47 = menos da metade por dia).',
            'Quando o preço cai, o ritmo sobe (1,29). Com o mesmo preço já cai para 0,76; com aumento acima de 30%, para 0,47.',
            'É correlação, não efeito comprovado do preço: o lote costuma virar logo depois de um pico e junto com o fim de promoções. Não use para decidir preço.',
          ]}
        />
      </Grade2>

      <Secao>Artistas e mercados</Secao>
      <Grade2>
        <GraficoFaixaLog
          titulo="O mesmo artista vende muito diferente entre estados"
          sub="Pagos medianos por evento na UF de menor e de maior venda · 2019+ · escala log"
          linhas={ARTISTA_UF}
          entenda={[
            'Bolinha laranja: mediana de pagos no estado onde o artista vende menos; azul: onde vende mais; traço: mediana geral. Cada marca do eixo vale cerca de três vezes a anterior.',
            `${ARTISTA_UF[0][0]} vai de ~${n(ARTISTA_UF[0][3])} a ~${n(ARTISTA_UF[0][4])} pagos por evento conforme o estado. Nos 204 artistas com eventos em 2 ou mais estados, o melhor estado vende em mediana 4,1 vezes o pior.`,
            'Local, preço, data e produtor também mudam entre estados. Não há ranking universal: a estimativa é sempre "este artista, neste mercado".',
          ]}
        />
        <div>
          <Texto>
            O banco não tem cadastro de artistas: pelo texto, o principal é identificado em 22% dos eventos; lendo os
            cartazes, chegaria a ~36%. No mesmo local, mais atrações não vendem mais (1,04×); o que pesa é o tamanho do
            local.
          </Texto>
          <Texto>
            Histórico do artista e Wikipedia não melhoraram a previsão de forma consistente. Antes da abertura das
            vendas, a previsão geral erra ~44% e só serve como faixa larga; ela dá lugar à projeção quando o evento chega a
            20 pagos.
          </Texto>
          <Kpis style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
            <Kpi rotulo="Ingressos por pedido" valor="1,65" dica="55% compram 1; 32% compram 2" />
            <Kpi rotulo="Pedidos do mesmo estado" valor="88%" dica="50% da mesma cidade" />
            <Kpi rotulo="Presença na portaria" valor="92%" dica="dos pagos, nos eventos com validação" />
          </Kpis>
        </div>
      </Grade2>

      <Secao>Produtores, concorrência e cancelamentos</Secao>
      <CartaoTabela style={{ marginBottom: 12 }}>
        <div className="titulo">
          <h3>Perfil do produtor (255 com ≥10 eventos)</h3>
        </div>
        <Rolagem>
          <Table>
            <thead>
              <tr>
                <th>Perfil</th>
                <th className="n">Produtores</th>
                <th className="n">Pagos por evento</th>
                <th className="n">% no dia</th>
                <th className="n">Erro da projeção em D-7</th>
              </tr>
            </thead>
            <tbody>
              {PERFIS_PRODUTOR.map(([perfil, produtores, pagos, noDia, erroD7]) => (
                <tr key={perfil} style={{ cursor: 'default' }}>
                  <td>{perfil}</td>
                  <td className="n">{produtores}</td>
                  <td className="n">{n(pagos)}</td>
                  <td className="n">{pctFmt(noDia)}</td>
                  <td className="n">{pctFmt(erroD7)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Rolagem>
      </CartaoTabela>
      <Entenda
        paragrafos={[
          'Lotes: 40% dos eventos usam um único lote. Eventos com mais lotes vendem mais (2,2× no mesmo produtor), mas o mais provável é que quem vende mais acabe criando mais lotes; não justifica recomendar "crie mais lotes". O degrau típico de preço entre lotes é +13%.',
          'Concorrência: 15% dos eventos dividem cidade e data com outro evento grande de outro produtor, sem queda detectável (1,04×).',
          'Primeira semana não serve como regra de bolso: o resultado final fica entre 1,7× e 53× o vendido na primeira semana.',
          'Cancelamentos caíram de 9,1% (2019) para 2,7% (2025–26); o PDV cancela 8,7%, contra 2,2% no site. Transferências subiram de 1,7% para 13% dos ingressos.',
        ]}
      />
    </>
  )
}
